import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchWxCallback,
  type WxUserInfo,
  type WxWechatUser,
} from "@/api/wechat";
import { bindAIChatSession } from "@/api/aiChat";
import { useUserStore } from "@/store/useUserStore";

/**
 * 微信授权回调页
 *
 * 流程：
 * 1. 用户在微信中完成授权后，微信会重定向到本页面，URL 携带 ?code=xxx&state=xxx&redirect=xxx
 * 2. 前端从 URL 取出 code，调用接口2: /home/login/wechat/callback
 * 3. 后端返回 token、用户信息(user)、微信用户信息(wechat_user)
 * 4. 前端存储用户信息和 token，跳转到 redirect 参数指定的目标页面
 */

// 默认授权成功跳转目标
const DEFAULT_REDIRECT = "/profile";

type CallbackStatus = "loading" | "exchanging" | "success" | "error";

export default function WxCallbackPage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const state = searchParams.get("state") || "";
  // 从 redirect 参数读取回跳地址（由 WxAuthGuard / wx-auth 页面传入）
  const redirectTarget = searchParams.get("redirect") || DEFAULT_REDIRECT;
  const [status, setStatus] = useState<CallbackStatus>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [userData, setUserData] = useState<WxUserInfo | null>(null);
  const [wxUser, setWxUser] = useState<WxWechatUser | null>(null);

  // Zustand 用户状态
  const login = useUserStore((state) => state.login);

  useEffect(() => {
    if (!code) {
      setStatus("error");
      setErrorMsg("未收到授权码，请重新尝试授权");
      return;
    }

    // 调用接口2：微信授权登录回调
    (async () => {
      try {
        setStatus("exchanging");

        const data = await fetchWxCallback(code, state);

        // 存储用户信息到 Zustand（自动持久化到 localStorage）
        login({
          token: data.token,
          tokenType: data.token_type,
          expiresIn: data.expires_in,
          user: data.user,
          wechatUser: data.wechat_user,
        });

        // 登录成功后绑定聊天会话到用户
        try {
          await bindAIChatSession();
          console.log("[WxCallback] 聊天会话绑定成功");
        } catch (err) {
          console.warn("[WxCallback] 聊天会话绑定失败:", err);
        }

        // 同时存储到 localStorage 供其他组件使用（兼容旧逻辑）
        localStorage.setItem("auth_token", data.token);
        localStorage.setItem("token_type", data.token_type || "Bearer");
        // 同步写入 supplier_token，供 peiwantHttp（BLW旧系统接口）认证使用
        localStorage.setItem("supplier_token", data.token);
        if (data.expires_in) {
          localStorage.setItem(
            "token_expires_at",
            String(Date.now() + data.expires_in * 1000),
          );
        }

        // 存储用户信息（供 profile 等页面使用）
        setUserData(data.user);
        setWxUser(data.wechat_user);

        // 合并存储：wechat_user_info 用于路由守卫检查
        localStorage.setItem(
          "wx_user_info",
          JSON.stringify({
            openid: data.wechat_user.openid,
            nickname: data.wechat_user.nickname || data.user.nickname,
            headimgurl: data.wechat_user.avatar || data.user.avatar,
            uid: data.user.uid,
          }),
        );
        localStorage.setItem("wx_openid", data.wechat_user.openid);
        localStorage.setItem("user_info", JSON.stringify(data.user));

        setStatus("success");

        // 延迟跳转，让用户看到成功态
        setTimeout(() => {
          window.location.href = redirectTarget;
        }, 1500);
      } catch (err) {
        alert(err);
        console.error("[WxCallback] 授权回调失败:", err);
        setStatus("error");
        setErrorMsg(
          err instanceof Error ? err.message : "授权过程中发生未知错误",
        );
      }
    })();
  }, [code, state, redirectTarget, login]);

  // 状态配置映射
  const statusConfig = {
    loading: {
      icon: "ri-loader-4-line animate-spin",
      iconColor: "#60A5FA",
      iconBg: "rgba(96,165,250,0.12)",
      title: "正在验证授权...",
      desc: "请稍候，我们正在确认您的授权状态",
    },
    exchanging: {
      icon: "ri-loader-4-line animate-spin",
      iconColor: "#07C160",
      iconBg: "rgba(7,193,96,0.12)",
      title: "正在获取用户信息...",
      desc: "正在从服务器获取您的个人信息",
    },
    success: {
      icon: "ri-check-double-fill",
      iconColor: "#10B981",
      iconBg: "rgba(16,185,129,0.12)",
      title: "登录成功！",
      desc:
        userData?.nickname || wxUser?.nickname
          ? `欢迎回来，${userData.nickname || wxUser.nickname}`
          : "即将为您跳转...",
    },
    error: {
      icon: "ri-close-circle-fill",
      iconColor: "#EF4444",
      iconBg: "rgba(239,68,68,0.12)",
      title: "授权失败",
      desc: errorMsg || "授权过程中发生错误",
    },
  };

  const config = statusConfig[status];

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{
        background:
          "linear-gradient(160deg, #0D0D1A 0%, #1A0A00 50%, #0D0D1A 100%)",
      }}
    >
      {/* 背景光晕 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute rounded-full"
          style={{
            width: 400,
            height: 400,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background:
              status === "error"
                ? "radial-gradient(circle, rgba(239,68,68,0.08) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(37,99,235,0.10) 0%, transparent 70%)",
          }}
        />
      </div>

      {/* 内容卡片 */}
      <div className="relative z-10 w-full max-w-sm text-center">
        {/* 状态图标 */}
        <div className="flex justify-center mb-6">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ background: config.iconBg }}
          >
            <i
              className={config.icon}
              style={{ color: config.iconColor, fontSize: 36 }}
            />
          </div>
        </div>

        {/* 标题 & 描述 */}
        <h2
          className="text-xl font-bold text-white mb-2"
          style={{ fontFamily: "'SF Pro Display', 'PingFang SC', sans-serif" }}
        >
          {config.title}
        </h2>
        <p className="text-sm mb-8" style={{ color: "rgba(255,255,255,0.45)" }}>
          {config.desc}
        </p>

        {/* 成功时显示用户信息卡片 */}
        {status === "success" && userData && (
          <div
            className="rounded-2xl p-5 mb-6 text-left"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div className="flex items-center gap-4">
              {(userData.avatar || wxUser?.avatar) && (
                <img
                  src={userData.avatar || wxUser?.avatar}
                  alt={userData.nickname || wxUser?.nickname}
                  className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-white truncate">
                  {userData.nickname || wxUser?.nickname || "用户"}
                </div>
                {userData.phone && (
                  <div
                    className="text-xs mt-1 truncate"
                    style={{ color: "rgba(255,255,255,0.35)" }}
                  >
                    {userData.phone}
                  </div>
                )}
                {wxUser && (
                  <div
                    className="text-xs mt-0.5"
                    style={{ color: "rgba(255,255,255,0.25)" }}
                  >
                    OpenID: {wxUser.openid.slice(0, 10)}...
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 错误时显示操作按钮 */}
        {status === "error" && (
          <div className="space-y-3">
            <button
              onClick={() =>
                (window.location.href = `/wx-auth?redirect=${encodeURIComponent(redirectTarget)}`)
              }
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl cursor-pointer active:scale-[0.97] transition-all"
              style={{
                background: "linear-gradient(135deg, #07C160, #06AE56)",
                boxShadow: "0 6px 24px rgba(7,193,96,0.28)",
                border: "none",
              }}
            >
              <i
                className="ri-refresh-line"
                style={{ color: "#fff", fontSize: 16 }}
              />
              <span className="text-sm font-semibold text-white">重新授权</span>
            </button>
            <button
              onClick={() => (window.location.href = "/")}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl cursor-pointer active:scale-[0.97] transition-all"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <i
                className="ri-home-line"
                style={{ color: "rgba(255,255,255,0.5)", fontSize: 16 }}
              />
              <span
                className="text-sm font-medium"
                style={{ color: "rgba(255,255,255,0.6)" }}
              >
                返回首页
              </span>
            </button>
          </div>
        )}

        {/* 加载中的提示条 */}
        {(status === "loading" || status === "exchanging") && (
          <div className="flex items-center justify-center gap-2">
            <div
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{
                background: "rgba(255,255,255,0.3)",
                animationDelay: "0s",
              }}
            />
            <div
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{
                background: "rgba(255,255,255,0.3)",
                animationDelay: "0.2s",
              }}
            />
            <div
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{
                background: "rgba(255,255,255,0.3)",
                animationDelay: "0.4s",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
